import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import {
  Typography,
  Card,
  Button,
  Spin,
  message,
  Modal,
  Input,
  Space,
  Checkbox,
  Alert,
} from 'antd';
import {
  WarningOutlined,
  DeleteOutlined,
  WalletOutlined,
  TagOutlined,
  FileExcelOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import Layout from '../../components/AdminLayout';
import { auth } from '@festpix/services';
import { colors } from '@festpix/ui';
import {
  verifyAdminPassword,
  deleteAllTransactions,
  resetAllBoothWalletBalances,
  resetAllSKUQuantities,
  getTransactionsCount,
  getBoothWalletsCount,
  getSKUsCount,
} from '../../services/dangerZoneService';
import {
  exportTransactions,
  exportBooths,
  exportSKUs,
} from '../../services/exportService';

const { Title, Text, Paragraph } = Typography;

type ActionType = 'transactions' | 'boothWallets' | 'skuQuantities';

interface DangerAction {
  key: ActionType;
  title: string;
  description: string;
  icon: React.ReactNode;
  buttonText: string;
  exportFn: () => Promise<void>;
  exportLabel: string;
  executeFn: () => Promise<number>;
  successMessage: (count: number) => string;
  getCount: () => Promise<number>;
}

const dangerActions: DangerAction[] = [
  {
    key: 'transactions',
    title: 'Excluir Todas as Transações',
    description:
      'Remove permanentemente todas as transações do sistema. Esta ação não pode ser desfeita.',
    icon: <DeleteOutlined style={{ fontSize: 24 }} />,
    buttonText: 'Excluir Transações',
    exportFn: exportTransactions,
    exportLabel: 'Baixar Relatório de Transações',
    executeFn: deleteAllTransactions,
    successMessage: (count) => `${count} transações excluídas com sucesso.`,
    getCount: getTransactionsCount,
  },
  {
    key: 'boothWallets',
    title: 'Zerar Saldos das Carteiras de Estandes',
    description:
      'Reseta o saldo de todas as carteiras de estandes para zero. Esta ação não pode ser desfeita.',
    icon: <WalletOutlined style={{ fontSize: 24 }} />,
    buttonText: 'Zerar Saldos',
    exportFn: exportBooths,
    exportLabel: 'Baixar Relatório de Estandes',
    executeFn: resetAllBoothWalletBalances,
    successMessage: (count) => `${count} carteiras de estandes zeradas com sucesso.`,
    getCount: getBoothWalletsCount,
  },
  {
    key: 'skuQuantities',
    title: 'Zerar Quantidades dos SKUs',
    description:
      'Reseta a quantidade de todos os SKUs (produtos) para zero. Esta ação não pode ser desfeita.',
    icon: <TagOutlined style={{ fontSize: 24 }} />,
    buttonText: 'Zerar Quantidades',
    exportFn: exportSKUs,
    exportLabel: 'Baixar Relatório de SKUs',
    executeFn: resetAllSKUQuantities,
    successMessage: (count) => `${count} SKUs com quantidades zeradas com sucesso.`,
    getCount: getSKUsCount,
  },
];

export default function DangerZonePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<DangerAction | null>(null);
  const [password, setPassword] = useState('');
  const [exportBeforeAction, setExportBeforeAction] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [itemCount, setItemCount] = useState<number | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/admin/login');
      } else {
        setUserEmail(user.email || '');
        setLoading(false);
      }
    });
    return unsubscribe;
  }, [navigate]);

  const openConfirmModal = async (action: DangerAction) => {
    setSelectedAction(action);
    setPassword('');
    setExportBeforeAction(true);
    setItemCount(null);
    setModalOpen(true);

    try {
      const count = await action.getCount();
      setItemCount(count);
    } catch (error) {
      console.error('Error getting count:', error);
    }
  };

  const handleExportOnly = async () => {
    if (!selectedAction) return;

    setExporting(true);
    try {
      await selectedAction.exportFn();
      message.success('Relatório exportado com sucesso!');
    } catch (error) {
      console.error('Export error:', error);
      message.error('Erro ao exportar relatório.');
    } finally {
      setExporting(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedAction || !password) {
      message.error('Por favor, insira sua senha.');
      return;
    }

    setExecuting(true);
    try {
      const verified = await verifyAdminPassword(userEmail, password);
      if (!verified) {
        message.error('Senha incorreta. Tente novamente.');
        setExecuting(false);
        return;
      }

      if (exportBeforeAction) {
        try {
          await selectedAction.exportFn();
          message.info('Relatório exportado antes da operação.');
        } catch (error) {
          console.error('Export error:', error);
        }
      }

      const count = await selectedAction.executeFn();
      message.success(selectedAction.successMessage(count));
      setModalOpen(false);
      setPassword('');
    } catch (error) {
      console.error('Execution error:', error);
      message.error('Erro ao executar a operação. Tente novamente.');
    } finally {
      setExecuting(false);
    }
  };

  const handleCancel = () => {
    setModalOpen(false);
    setSelectedAction(null);
    setPassword('');
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Title level={2} style={{ marginBottom: 8, color: colors.error }}>
        <WarningOutlined style={{ marginRight: 12 }} />
        Danger Zone
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Operações destrutivas e irreversíveis. Prossiga com extrema cautela.
      </Text>

      <Alert
        message="Atenção"
        description="As operações abaixo são permanentes e não podem ser desfeitas. Recomendamos exportar os dados antes de realizar qualquer ação."
        type="warning"
        showIcon
        icon={<ExclamationCircleOutlined />}
        style={{ marginBottom: 24 }}
      />

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {dangerActions.map((action) => (
          <Card
            key={action.key}
            style={{
              border: `2px solid ${colors.error}`,
              borderRadius: 8,
              background: colors.cardBackground,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 16,
              }}
            >
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <span style={{ color: colors.error }}>{action.icon}</span>
                  <Title level={5} style={{ margin: 0 }}>
                    {action.title}
                  </Title>
                </div>
                <Text type="secondary">{action.description}</Text>
              </div>
              <Button
                danger
                type="primary"
                icon={<WarningOutlined />}
                onClick={() => openConfirmModal(action)}
                size="large"
              >
                {action.buttonText}
              </Button>
            </div>
          </Card>
        ))}
      </Space>

      <Modal
        title={
          <span style={{ color: colors.error }}>
            <WarningOutlined style={{ marginRight: 8 }} />
            Confirmar Operação Destrutiva
          </span>
        }
        open={modalOpen}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
      >
        {selectedAction && (
          <div>
            <Alert
              message={selectedAction.title}
              description={selectedAction.description}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />

            {itemCount !== null && (
              <Paragraph>
                <Text strong>Itens afetados: </Text>
                <Text>{itemCount}</Text>
              </Paragraph>
            )}

            <div
              style={{
                background: '#fafafa',
                padding: 16,
                borderRadius: 8,
                marginBottom: 16,
              }}
            >
              <Checkbox
                checked={exportBeforeAction}
                onChange={(e) => setExportBeforeAction(e.target.checked)}
              >
                Exportar dados antes de executar a operação
              </Checkbox>
              <div style={{ marginTop: 8 }}>
                <Button
                  icon={<FileExcelOutlined />}
                  onClick={handleExportOnly}
                  loading={exporting}
                  size="small"
                >
                  {selectedAction.exportLabel}
                </Button>
              </div>
            </div>

            <Paragraph>
              <Text strong>Para confirmar, insira sua senha:</Text>
            </Paragraph>
            <Input.Password
              placeholder="Sua senha"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ marginBottom: 16 }}
              onPressEnter={handleConfirm}
            />

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button onClick={handleCancel} disabled={executing}>
                Cancelar
              </Button>
              <Button
                danger
                type="primary"
                onClick={handleConfirm}
                loading={executing}
                disabled={!password}
              >
                Confirmar e Executar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </Layout>
  );
}
